import { useRef, useState } from "react";
import { LuCloudUpload, LuFileText, LuX } from "react-icons/lu";
import RadioButtonControlGroup from "../../../../../shared/RadioButtonControlGroup";
import "./SetupStep.scss";

/**
 * Maximum accepted upload size for SPD/SBC PDFs (50MB).
 * @constant {number}
 */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

/**
 * Document-type tabs on the Setup step.
 * @readonly
 * @type {ReadonlyArray<{ value: string, label: string, longLabel: string }>}
 */
export const DOCUMENT_TYPE_TABS = Object.freeze([
    {
        value: "spd",
        label: "SPD",
        longLabel: "SPD (Summary Plan Description)",
    },
    {
        value: "sbc",
        label: "SBC",
        longLabel: "SBC (Summary of Benefits and Coverage)",
    },
]);

/**
 * Upload-size helper text shown under the dropzone.
 * @constant {string}
 */
export const UPLOAD_LIMIT_LABEL = "PDF only · max 50MB per file";

/**
 * Short intro explaining the Setup step purpose.
 * @constant {string}
 */
export const SETUP_STEP_INTRO =
    "Upload the plan’s SPD and/or SBC as PDFs. Documents are optional and independent — choose a type, then drag a file here or browse.";

/**
 * Validates a candidate SPD/SBC upload.
 * @param {File | null | undefined} file
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export const validatePlanSetupPdf = (file) => {
    if (!file) {
        return { ok: false, message: "Choose a PDF file to upload." };
    }

    const isPdf =
        file.type === "application/pdf" || /\.pdf$/i.test(file.name ?? "");

    if (!isPdf) {
        return { ok: false, message: "Only PDF files are allowed." };
    }

    if (file.size > MAX_UPLOAD_BYTES) {
        return { ok: false, message: "File must be 50MB or smaller." };
    }

    return { ok: true };
};

/**
 * @param {string} docType
 * @returns {string}
 */
const fileFieldForDocType = (docType) => `${docType}FileName`;

/**
 * Setup step. Presentational — document-type tabs, dual-doc status, and a
 * drag-drop/browse zone with client-side PDF/size validation. Valid files are
 * handed to `onUploadDocument`, which uploads to S3 and records the file name
 * and object URL in page state. Owns ephemeral drag/error UI state only.
 *
 * @param {object} props
 * @param {Object} props.values - The Setup slice of the wizard form state.
 * @param {Record<string, string>} [props.errors] - Field-level validation errors.
 * @param {(field: string, value: string) => void} props.onFieldChange - Emits a field change.
 * @param {(docType: string, file: File) => Promise<void>} props.onUploadDocument - Uploads a validated PDF; rejects with a user-facing message.
 * @param {(docType: string) => void} props.onRemoveDocument - Clears a document's file name and URL.
 * @param {string | null} [props.uploadingDocType] - Document type currently uploading, if any.
 * @returns {import('react').JSX.Element}
 */
const SetupStep = ({
    values,
    errors = {},
    onFieldChange,
    onUploadDocument,
    onRemoveDocument,
    uploadingDocType = null,
}) => {
    const fileInputRef = useRef(null);
    const dragDepthRef = useRef(0);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadError, setUploadError] = useState("");

    const activeDocType = values.activeDocType;
    const activeTab =
        DOCUMENT_TYPE_TABS.find((tab) => tab.value === activeDocType) ??
        DOCUMENT_TYPE_TABS[0];
    const activeFileField = fileFieldForDocType(activeDocType);
    const activeFileName = values[activeFileField];
    const activeFileUrl = values[`${activeDocType}Url`];
    const isUploading = Boolean(uploadingDocType);
    const isActiveUploading = uploadingDocType === activeDocType;

    const tabOptions = DOCUMENT_TYPE_TABS.map((tab) => {
        const hasFile = Boolean(values[fileFieldForDocType(tab.value)]);

        return {
            value: tab.value,
            label: tab.label,
            id: `doc-tab-${tab.value}`,
            controls: `doc-panel-${tab.value}`,
            tone: hasFile ? "green" : "default",
        };
    });

    const acceptFile = async (file) => {
        if (isUploading) {
            return;
        }

        const result = validatePlanSetupPdf(file);

        if (!result.ok) {
            setUploadError(result.message);
            return;
        }

        setUploadError("");
        try {
            await onUploadDocument(activeDocType, file);
        } catch (error) {
            setUploadError(error?.message || "Failed to upload document.");
        }
    };

    const handleBrowse = (event) => {
        const file = event.target.files?.[0];
        acceptFile(file);
        event.target.value = "";
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragDepthRef.current += 1;
        setIsDragging(true);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = "copy";
        }
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setIsDragging(false);
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragDepthRef.current = 0;
        setIsDragging(false);

        const file = event.dataTransfer?.files?.[0];
        acceptFile(file);
    };

    const handleDocTypeChange = (value) => {
        setUploadError("");
        onFieldChange("activeDocType", value);
    };

    const dropzoneClassName = [
        "plan-setup-setup-step__dropzone",
        isDragging ? "is-dragging" : null,
        uploadError ? "has-error" : null,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className="plan-setup-step plan-setup-setup-step">
            <p className="plan-setup-setup-step__intro">{SETUP_STEP_INTRO}</p>

            <div className="plan-setup-setup-step__doc-type">
                <p className="plan-setup-setup-step__doc-type-label" id="doc-type-label">
                    Document type
                </p>
                <RadioButtonControlGroup
                    role="tablist"
                    ariaLabelledBy="doc-type-label"
                    value={activeDocType}
                    onChange={handleDocTypeChange}
                    options={tabOptions}
                    minCh="8ch"
                />
                <p className="plan-setup-setup-step__doc-type-detail">
                    Uploading: <strong>{activeTab.longLabel}</strong>
                </p>
            </div>

            <ul className="plan-setup-setup-step__status" aria-label="Document upload status">
                {DOCUMENT_TYPE_TABS.map((tab) => {
                    const fileName = values[fileFieldForDocType(tab.value)];
                    const isActive = tab.value === activeDocType;
                    const fieldError = errors[fileFieldForDocType(tab.value)];

                    return (
                        <li key={tab.value}>
                            <button
                                type="button"
                                className={[
                                    "plan-setup-setup-step__status-item",
                                    isActive ? "is-active" : null,
                                    fileName ? "has-file" : null,
                                    fieldError ? "has-error" : null,
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                                onClick={() => handleDocTypeChange(tab.value)}
                                aria-current={isActive ? "true" : undefined}
                            >
                                <span className="plan-setup-setup-step__status-type">
                                    {tab.label}
                                </span>
                                <span className="plan-setup-setup-step__status-value">
                                    {fileName || "Not uploaded"}
                                </span>
                                {fieldError ? (
                                    <span className="plan-setup-setup-step__status-error">
                                        {fieldError}
                                    </span>
                                ) : null}
                            </button>
                        </li>
                    );
                })}
            </ul>

            <div
                className={dropzoneClassName}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <LuCloudUpload aria-hidden size={48} />
                <p className="plan-setup-setup-step__dropzone-title">
                    {isActiveUploading
                        ? `Uploading ${activeTab.label}...`
                        : isDragging
                          ? `Drop ${activeTab.label} PDF here`
                          : `Drag and drop ${activeTab.label} PDF here`}
                </p>
                <p className="plan-setup-setup-step__dropzone-or">or</p>
                <button
                    type="button"
                    className="plan-setup-setup-step__browse"
                    disabled={isUploading}
                    onClick={() => fileInputRef.current?.click()}
                >
                    Browse files
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    className="plan-setup-setup-step__file-input"
                    onChange={handleBrowse}
                />
            </div>

            <p className="plan-setup-setup-step__limit">{UPLOAD_LIMIT_LABEL}</p>

            {uploadError ? (
                <p className="plan-setup-setup-step__error" role="alert">
                    {uploadError}
                </p>
            ) : null}

            <div
                id={`doc-panel-${activeDocType}`}
                role="tabpanel"
                aria-labelledby={`doc-tab-${activeDocType}`}
                className="plan-setup-setup-step__panel"
            >
                {activeFileName ? (
                    <div className="plan-setup-setup-step__file">
                        <LuFileText
                            aria-hidden
                            size={18}
                            className="plan-setup-setup-step__file-icon"
                        />
                        {activeFileUrl ? (
                            <a
                                href={activeFileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="plan-setup-setup-step__file-name plan-setup-setup-step__file-link"
                                title={`Open ${activeFileName} in a new tab`}
                            >
                                {activeFileName}
                            </a>
                        ) : (
                            <span className="plan-setup-setup-step__file-name">
                                {activeFileName}
                            </span>
                        )}
                        <button
                            type="button"
                            className="plan-setup-setup-step__remove"
                            aria-label={`Remove ${activeFileName}`}
                            disabled={isActiveUploading}
                            onClick={() => {
                                setUploadError("");
                                onRemoveDocument(activeDocType);
                            }}
                        >
                            <LuX aria-hidden size={16} />
                        </button>
                    </div>
                ) : (
                    <p className="plan-setup-setup-step__empty">
                        No {activeTab.label} file selected yet.
                    </p>
                )}
            </div>
        </div>
    );
};

export default SetupStep;
